import { Inject, Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { FaresActions } from '../../actions';
import { FareRequest, GetFareResponse, TicketProduct } from '../../client/src';
import {
  catchError,
  mergeMap,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { of } from 'rxjs';
import { ICorrelationParams, LoggingService } from '@nx-ng/types';
import { TicketsAndFaresStoreFacade } from '../../facade';
import { Action } from '@ngrx/store';
import { TicketsAndFaresService } from '@modules/ticket-fares-store/services/tickets-and-fares.service';
import { isApiError, isNullOrUndefined } from '@nx-ng/util';
import { ApiError } from '@modules/addresses-store/client/src';
import { LOGGER } from '@modules/core';

@Injectable()
export class FaresEffects {
  loadFlexiFares$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FaresActions.loadFlexiFares),
      switchMap(() => {
        return this.ticketsAndFaresService.loadFlexibleFares().pipe(
          map((response?: GetFareResponse) => {
            if (!response) {
              return FaresActions.loadFlexiFaresFailure({
                error: 'No data has been returned',
                retryAction: FaresActions.loadFlexiFares(),
              });
            }

            if (isApiError(response)) {
              return FaresActions.loadFlexiFaresFailure({
                error: response,
                retryAction: FaresActions.loadFlexiFares(),
              });
            }

            return FaresActions.loadFlexiFaresSuccess({
              fareResponse: response,
            });
          }),
          catchError((error) => {
            const apiError: ApiError | string =
              error?.response?.parsedBody || error;

            this.logger.logApiError(
              {
                apiError,
              },
              error
            );
            return of(
              FaresActions.loadFlexiFaresFailure({
                error: apiError,
                retryAction: FaresActions.loadFlexiFares(),
              })
            );
          })
        );
      })
    )
  );

  loadPaygFares$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FaresActions.loadPaygFares),
      switchMap(() => {
        return this.ticketsAndFaresService.loadPaygFares().pipe(
          map((response?: GetFareResponse) => {
            if (!response) {
              return FaresActions.loadPaygFaresFailure({
                error: 'No data has been returned',
                retryAction: FaresActions.loadPaygFares(),
              });
            }
            return FaresActions.loadPaygFaresSuccess({
              fareResponse: response,
            });
          }),
          catchError((error) => {
            const apiError: ApiError | string =
              error?.response?.parsedBody || error;

            this.logger.logApiError(
              {
                apiError,
              },
              error
            );
            return of(
              FaresActions.loadPaygFaresFailure({
                error: apiError,
                retryAction: FaresActions.loadPaygFares(),
              })
            );
          })
        );
      })
    )
  );

  searchFares$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FaresActions.searchFares),
      withLatestFrom(this.TicketsAndFaresStoreFacade.selectedValues$),
      switchMap(([_, fareRequest]: [Action, Partial<FareRequest>]) => {
        if (
          isNullOrUndefined(fareRequest.customerType) ||
          isNullOrUndefined(fareRequest.travelMethod) ||
          isNullOrUndefined(fareRequest.travelTime) ||
          isNullOrUndefined(fareRequest.zone)
        ) {
          return of(
            FaresActions.searchFaresFailure({
              error: `Not all search properties have been specified: ${JSON.stringify(
                fareRequest
              )}`,
              retryAction: FaresActions.searchFares(),
            })
          );
        }
        return this.ticketsAndFaresService
          .searchFares(fareRequest as FareRequest)
          .pipe(
            map((response?: GetFareResponse) => {
              if (!response) {
                return FaresActions.searchFaresFailure({
                  error: 'No data has been returned',
                  retryAction: FaresActions.searchFares(),
                });
              }
              return FaresActions.searchFaresSuccess({
                fareResponse: response,
              });
            }),
            catchError((error) => {
              const apiError: ApiError | string =
                error?.response?.parsedBody ||
                'searchFares API call has failed';

              this.logger.logApiError(
                {
                  apiError,
                },
                error
              );
              return of(
                FaresActions.searchFaresFailure({
                  error: apiError,
                  retryAction: FaresActions.searchFares(),
                })
              );
            })
          );
      })
    )
  );

  loadFareById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FaresActions.loadFareById),
      mergeMap(
        (
          action: {
            ticketID: string;
            correlationParams?: ICorrelationParams;
            quantity?: number;
          } & Action
        ) => {
          const { ticketID, correlationParams, quantity } = action;
          return this.ticketsAndFaresService.loadFareById(ticketID).pipe(
            map((response?: TicketProduct) => {
              if (!response) {
                return FaresActions.loadFareByIdFailure({
                  error: 'No data has been returned',
                  retryAction: action,
                });
              }
              return FaresActions.loadFareByIdSuccess({
                fareResponse: response,
                correlationParams,
              });
            }),
            catchError((error) => {
              const apiError: ApiError | string =
                error?.response?.parsedBody ||
                'loadFareById API call has failed';

              this.logger.logApiError(
                {
                  apiError,
                },
                error
              );

              return of(
                FaresActions.loadFareByIdFailure({
                  error: apiError,
                  retryAction: action,
                })
              );
            })
          );
        }
      )
    )
  );

  loadFareByAlias$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FaresActions.loadFareByAlias),
      switchMap(
        (action: { ticketAlias: string; basketId?: string } & Action) => {
          const { ticketAlias, basketId } = action;
          return this.ticketsAndFaresService.loadFareByALias(ticketAlias).pipe(
            map((response?: TicketProduct) => {
              if (!response) {
                return FaresActions.loadFareByIdFailure({
                  error: 'No data has been returned',
                });
              }
              return FaresActions.loadFareByAliasSuccess({
                fareResponse: response,
                basketId,
              });
            }),
            catchError((error) => {
              const apiError: ApiError | string =
                error?.response?.parsedBody ||
                'loadFareByAlias API call has failed';
              return of(
                FaresActions.loadFareByALiasFailure({
                  error: apiError,
                  retryAction: action,
                })
              );
            })
          );
        }
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private TicketsAndFaresStoreFacade: TicketsAndFaresStoreFacade,
    private ticketsAndFaresService: TicketsAndFaresService,
    @Inject(LOGGER) private logger: LoggingService
  ) {}
}
